'use client';

import type { FC, ImgHTMLAttributes } from 'react';
import type { BadgeItem } from '@weberus/shared-types';
import type { ThumbnailImgProps } from '@yanolja-rnd/nol-design-system';
import type { ThumbnailRatioType } from '@yanolja-rnd/nol-design-system/dist/components/thumbnail/thumbnail.types';
import { MembershipBadge, ProductBadge, Thumbnail } from '@yanolja-rnd/nol-design-system';

/*
 * Thumbnail
 * 사용 영역: 국내숙소, 레저, 해외숙소
 * 타입 정보: List
 */

type RatioType = Extract<ThumbnailRatioType, '2_1' | '3_4'>;

export interface UnitThumbnailProps {
  url: string;
  ratio?: RatioType;
  badges?: BadgeItem[] | null;
  alt: string;
}

const ratioSizeMap: Record<RatioType, { width: number; height: number }> = {
  '2_1': { width: 328, height: 164 },
  '3_4': { width: 126, height: 168 },
};

export const createCustomImage = (imgProps: ImgHTMLAttributes<HTMLImageElement>) => {
  const WrappedCustomImage: FC<ThumbnailImgProps> = (props) => {
    const { alt, className, src, ...rest } = props;
    return <img src={src} alt={alt} className={className} {...imgProps} {...rest} />;
  };
  return WrappedCustomImage;
};

export const UnitThumbnail: FC<UnitThumbnailProps> = (props) => {
  const { url, ratio = '3_4', alt, badges } = props;
  const size = ratioSizeMap[ratio];
  return (
    <div className="relative">
      <Thumbnail
        ratio={ratio}
        shape="roundedRectangle_8"
        image={url}
        alt={alt}
        imageComponent={createCustomImage({ ...size })}
      />
      {/* 뱃지 렌더링 */}
      {!!badges && (
        <div className="absolute left-6 top-6 flex flex-col items-start gap-2">
          {badges.map((badge, idx) => (
            <BadgeRenderer key={idx} badge={badge} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/~jusong.lee/pages/491947083/PLP+SRP#Enum---Badges}
 * */
const BadgeRenderer: FC<{ badge: BadgeItem }> = ({ badge }) => {
  const { type, text = '' } = badge;
  switch (type) {
    case 'THUMBNAIL_MEMBER_CLASS_GOLD':
      return (
        <MembershipBadge type="gold_special_price" size="medium">
          {text}
        </MembershipBadge>
      );
    case 'THUMBNAIL_PRIMARY':
    default:
      return <ProductBadge size="medium">{text}</ProductBadge>;
  }
};
